import React from 'react';
import { useDispatch } from 'react-redux';

import Content from '../../components/Content';
import Loader from '../../components/Loader';
import Table, { SortableTh } from '../../components/Table';
import useSort from '../../components/Table/useSort';
import SelectBox from '../../components/SelectBox';
import Pagination from '../../components/Pagination';
import NetworkError from '../../components/Error';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import { getAssetBaselineInitials, getAssetBaselineData } from '../../redux/actions/watcherActions';
import PortBaselineModal from './PortBaselineModal';

import styles from './AssetBaseline.module.css';

const FRONTEND_ASSET_BASELINE_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	DATA_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildAssetBaselineSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const AssetBaselineScreen = () => {
	const dispatch = useDispatch();

	const [selectedWatcher, setSelectedWatcher] = React.useState('');
	const [searchKey, setSearchKey] = React.useState('');

	const [dataLoading, setDataLoading] = React.useState(false);
	const [watcherDataInitials, setWatcherDataInitials] = React.useState({});
	const [assetRows, setAssetRows] = React.useState([]);
	const [assetColumns, setAssetColumns] = React.useState([]);
	const [hasError, setHasError] = React.useState(false);
	const [supportErrorMessage, setSupportErrorMessage] = React.useState('');
	const [copiedSupportError, setCopiedSupportError] = React.useState(false);

	const [pageNumber, setPageNumber] = React.useState(1);
	const pageLimit = 20;

	const [showPortBaselineModal, setShowPortBaselineModal] = React.useState(false);
	const [selectedDeviceIP, setSelectedDeviceIP] = React.useState('');
	const [selectedWatcherId, setSelectedWatcherId] = React.useState('');

	const setAssetBaselineSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildAssetBaselineSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearAssetBaselineSupportError = React.useCallback((force = false) => {
		if (!force) return;
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [supportErrorMessage]);

	React.useEffect(
		() => {
			const getData = async () => {
				setDataLoading(true);
				setHasError(false);

				try {
					const data = await dispatch(getAssetBaselineInitials());

					if (data && data.status) {
						setWatcherDataInitials(data);
						clearAssetBaselineSupportError(true);
						setHasError(false);
					} else {
						setWatcherDataInitials({});
						setAssetColumns([]);
						setAssetRows([]);
						setHasError(true);
						setAssetBaselineSupportError(
							FRONTEND_ASSET_BASELINE_ERROR_CODES.INITIAL_LOAD_FAILED,
							'Asset Baseline Page / Initial Data Load Failed',
							data?.msg || (typeof data === 'string' ? data : 'Asset baseline initials request did not return success')
						);
					}
				} catch (error) {
					setWatcherDataInitials({});
					setAssetColumns([]);
					setAssetRows([]);
					setHasError(true);
					setAssetBaselineSupportError(
						FRONTEND_ASSET_BASELINE_ERROR_CODES.INITIAL_LOAD_FAILED,
						'Asset Baseline Page / Initial Data Request Error',
						error?.message || String(error)
					);
				} finally {
					setDataLoading(false);
				}
			};

			getData();
		},
		[clearAssetBaselineSupportError, dispatch, setAssetBaselineSupportError]
	);

	React.useEffect(
		() => {
			let cancelled = false;
			const getData = async () => {
				setDataLoading(true);
				setAssetRows([]);   // clear the previous watcher's rows immediately on switch

				const formData = new FormData();
				formData.append('watchers', selectedWatcher);

				try {
					const data = await dispatch(getAssetBaselineData(formData));
					if (cancelled) return;   // superseded by a newer selection

					if (data && data.status) {
						setAssetColumns(Array.isArray(data.BaselineCols) ? data.BaselineCols : []);
						setAssetRows(Array.isArray(data.BaselineRows) ? data.BaselineRows : []);
						clearAssetBaselineSupportError(true);
						setHasError(false);
					} else {
						setHasError(true);
						setAssetBaselineSupportError(
							FRONTEND_ASSET_BASELINE_ERROR_CODES.DATA_LOAD_FAILED,
							'Asset Baseline Page / Baseline Data Load Failed',
							data?.msg || (typeof data === 'string' ? data : 'Asset baseline data request did not return success')
						);
					}
				} catch (error) {
					setHasError(true);
					setAssetBaselineSupportError(
						FRONTEND_ASSET_BASELINE_ERROR_CODES.DATA_LOAD_FAILED,
						'Asset Baseline Page / Baseline Data Request Error',
						error?.message || String(error)
					);
				} finally {
					setDataLoading(false);
					setPageNumber(1);
				}
			};

			if (selectedWatcher) {
				getData();
			}
			return () => { cancelled = true; };
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selectedWatcher, dispatch]
	);

	// No auto-select: the page renders with the dropdown populated but no data
	// until the user picks a watcher. The data effect above early-returns while
	// selectedWatcher is empty.

	const filteredAssetRows = React.useMemo(() => {
		if (!searchKey) return assetRows;

		return assetRows.filter((row) =>
			row.some((cell) => cell && cell.toString().toLowerCase().includes(searchKey.toLowerCase()))
		);
	}, [assetRows, searchKey]);

	// Client-side column sort keyed by column index (rows are arrays). Sort the
	// searched set, then paginate the sorted result below.
	const { sorted: sortedAssetRows, sortKey, sortDir, requestSort } = useSort(filteredAssetRows);
	const sort = { sortKey, sortDir, requestSort };

	const paginatedAssetRows = React.useMemo(() => {
		const startIndex = (pageNumber - 1) * pageLimit;
		const endIndex = startIndex + pageLimit;
		return sortedAssetRows.slice(startIndex, endIndex);
	}, [sortedAssetRows, pageNumber, pageLimit]);

	// Reset to the first page whenever the sort changes underfoot.
	React.useEffect(() => {
		setPageNumber(1);
	}, [sortKey, sortDir]);

	const handleAssetClick = (rowData, columnIndex) => {
		if (columnIndex === 2) {
			const deviceIP = rowData[2];

			setSelectedDeviceIP(deviceIP);
			setSelectedWatcherId(selectedWatcher);
			setShowPortBaselineModal(true);
		}
	};

	const closePortBaselineModal = () => {
		setShowPortBaselineModal(false);
		setSelectedDeviceIP('');
		setSelectedWatcherId('');
	};

	const handleWatcherChange = (e) => {
		setSelectedWatcher(e.target.value);
	};

	const handleSearchKeyChange = (e) => {
		setSearchKey(e.target.value);
		setPageNumber(1);
	};

	const handlePageChange = (selected) => {
		setPageNumber(selected.selected + 1);
	};

	const showErrorView = hasError && !dataLoading && filteredAssetRows.length === 0;

	return (
		<Content
			currentMenu='asset-baseline'
			headerTitle='Asset Baseline'
			searchBox={true}
			searchPlaceholder='Search Asset Baseline'
			searchValue={searchKey}
			searchOnChange={handleSearchKeyChange}
		>
			<div className={styles.header_controls}>
				<div className={styles.watcher_selector}>
					<SelectBox label='Select Watcher' value={selectedWatcher} onChange={handleWatcherChange}>
						{watcherDataInitials && watcherDataInitials.watchers && watcherDataInitials.watchers.length > 0 ? (
							[
								<option value='' key='placeholder'>Select a Watcher</option>,
								<option value='All Watchers' key='all'>All Watchers</option>,
								...watcherDataInitials.watchers.map((watcher) =>
									watcher.length >= 2 ? (
										<option value={watcher[0]} key={watcher[0]}>
											{watcher[1]}
										</option>
									) : null
								),
							]
						) : (
							<option value=''>No watchers found</option>
						)}
					</SelectBox>
				</div>
			</div>

			{dataLoading ? (
				<Loader />
			) : showErrorView ? (
				<div>
					<NetworkError />
					{supportErrorMessage && (
						<div className={styles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={styles.supportErrorCode}>{supportErrorMessage}</code>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button type='button' className={styles.copySupportErrorButton} onClick={handleCopySupportError}>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={styles.copySupportErrorButton}
									onClick={() => clearAssetBaselineSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
				</div>
			) : (
				<>
					{supportErrorMessage && (
						<div className={styles.supportErrorBanner}>
							<div className={styles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={styles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button type='button' className={styles.copySupportErrorButton} onClick={handleCopySupportError}>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={styles.copySupportErrorButton}
									onClick={() => clearAssetBaselineSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
					<div className={styles.table_container}>
						<Table
							containerStyle={{ height: '100%', flex: '1 1 auto', overflow: 'auto' }}
							tableHead={
								<tr>
									{assetColumns && assetColumns.length > 0 &&
										assetColumns.map((col, index) => (
											<SortableTh key={col + index} label={col} field={index} sort={sort} />
										))}
								</tr>
							}
							tableRow={
								paginatedAssetRows && paginatedAssetRows.length > 0 ? (
									paginatedAssetRows.map((rows, rowIndex) => (
										<tr key={`asset-row-${rowIndex}`}>
											{rows && rows.length > 0 &&
												rows.map((cell, cellIndex) => (
													<td
														key={`asset-cell-${rowIndex}-${cellIndex}`}
														onClick={() => handleAssetClick(rows, cellIndex)}
														style={cellIndex === 2 ? { cursor: 'pointer', color: '#007bff' } : {}}
													>
														{cell}
													</td>
												))}
										</tr>
									))
								) : (
									<tr>
										<td colSpan={assetColumns ? assetColumns.length : 5} style={{ textAlign: 'center' }}>
											No asset baseline data available for the selected watcher.
										</td>
									</tr>
								)
							}
						/>
					</div>

					{filteredAssetRows.length > pageLimit && (
						<div className='paginationContainer'>
							<Pagination
								onPageChange={handlePageChange}
								rowsPerPage={pageLimit}
								pageNumber={pageNumber}
								totalPageSize={filteredAssetRows.length}
							/>
						</div>
					)}

					{filteredAssetRows.length === 0 && !dataLoading && !hasError && (
						<div className={styles.no_data}>
							<p>No asset baseline data available for the selected watcher.</p>
						</div>
					)}
				</>
			)}

			<PortBaselineModal
				isOpen={showPortBaselineModal}
				onClose={closePortBaselineModal}
				deviceIP={selectedDeviceIP}
				watcherId={selectedWatcherId}
			/>
		</Content>
	);
};

export default AssetBaselineScreen;
